import type { MotivoDeParada, VerticalDaCampanha, VerticalDoLote } from '../../api/cptu';
import { coordenada, numero, paraCampo, paraNumero } from '../../formato';
import { textoDoEncerramento } from '../../api/cptu';

/**
 * A Edição rápida das verticais — onda 333 (pedido do Humberto em 16/10:
 * «em verticais, não há um local para definir as coordenadas de cada
 * vertical. Como o quick edit que solicitei»).
 *
 * O que é puro mora aqui (e o teste `320.0x` o carrega sem navegador): ler o
 * número e o grau como a planilha brasileira e o GPS os escrevem, distribuir
 * um bloco colado do Excel pela grade, validar cada célula com a frase em
 * português e montar o corpo do lote com só o que mudou.
 */

/** As colunas da grade que recebem texto (e a colagem), de todos os sistemas. */
export const COLUNAS_DE_TEXTO = ['na', 'cota', 'lat', 'lon', 'e', 'n'] as const;
export type ColunaDeTexto = (typeof COLUNAS_DE_TEXTO)[number];

export const NOME_DA_COLUNA: Record<ColunaDeTexto, string> = {
  na: 'NA (m)', cota: 'Cota (m)', lat: 'Latitude', lon: 'Longitude', e: 'E (m)', n: 'N (m)',
};

/**
 * Onda 334 (Humberto, 16/10: «Nas coordenadas, permita coordenadas UTM também. Igual as amostras de
 * Laboratório»): o sistema da coordenada vale para a GRADE inteira, como na Edição rápida das amostras —
 * quem digita a planilha de locação está no mesmo sistema e no mesmo fuso de todas as verticais. UTM é
 * SIRGAS 2000, hemisfério sul, fusos 18 a 25 (os do Brasil); quem converte é o banco (0187).
 */
export type ModoDaCoordenada = 'utm' | 'graus';
export interface SistemaDaCoordenada { modo: ModoDaCoordenada; fuso: number }
export const FUSOS = [18, 19, 20, 21, 22, 23, 24, 25] as const;

/** As colunas da grade no sistema escolhido, na ordem da tela (e da colagem). */
export function colunasDoModo(modo: ModoDaCoordenada): ColunaDeTexto[] {
  return modo === 'utm' ? ['na', 'cota', 'e', 'n'] : ['na', 'cota', 'lat', 'lon'];
}

/**
 * O fuso de partida: o que mais aparece entre as verticais com coordenada; sem nenhuma, o 22 (Blumenau e
 * quase toda a Santa Catarina, como nas amostras).
 */
export function fusoDePartida(vs: Pick<VerticalDaCampanha, 'utm_fuso' | 'utm_e'>[]): number {
  const conta = new Map<number, number>();
  for (const v of vs) {
    if (v.utm_e === null || v.utm_fuso === null || v.utm_fuso < 18 || v.utm_fuso > 25) continue;
    conta.set(v.utm_fuso, (conta.get(v.utm_fuso) ?? 0) + 1);
  }
  let melhor = 22;
  let n = 0;
  for (const [f, k] of conta) if (k > n || (k === n && f === 22)) { melhor = f; n = k; }
  return melhor;
}

/** O E e o N gravados, no fuso da grade; null quando não há coordenada ou ela está noutro fuso. */
export function utmGravado(v: Pick<VerticalDaCampanha, 'utm_fuso' | 'utm_e' | 'utm_n'>, fuso: number):
  { e: number; n: number } | null {
  return v.utm_e !== null && v.utm_n !== null && v.utm_fuso === fuso ? { e: v.utm_e, n: v.utm_n } : null;
}

/** A vertical tem coordenada, mas noutro fuso: a grade diz qual, em vez de mostrar a célula vazia calada. */
export function outroFuso(v: Pick<VerticalDaCampanha, 'utm_fuso' | 'utm_e' | 'latitude'>, fuso: number): number | null {
  return v.latitude !== null && v.utm_fuso !== null && v.utm_fuso !== fuso ? v.utm_fuso : null;
}

/** O rascunho de uma vertical na grade: o texto de cada campo, o cone e o encerramento. */
export interface RascunhoDaVertical {
  na: string;
  cota: string;
  lat: string;
  lon: string;
  /** Onda 334: a coordenada em UTM, no fuso da grade. */
  e: string;
  n: string;
  cone: { id: string | null; rotulo: string | null };
  parada: MotivoDeParada | '';
  paradaTexto: string;
}

type ConeDaVertical = Pick<VerticalDaCampanha, 'cone_equipamento_id' | 'cone_patrimonio' | 'cone_nome'
  | 'cone_numero_serie' | 'cone_codigo' | 'cone_pelo_numero' | 'cone_numero_ambiguo'>;

/**
 * O cone como a ficha o diz: o do parque; o que o número do arquivo achou no parque (onda 335 — «nº 1378 · achado
 * pelo número do arquivo», sem ninguém escolher); ou o número do arquivo que o parque não conhece.
 */
export function textoDoCone(v: ConeDaVertical): string | null {
  if (v.cone_equipamento_id) {
    return [v.cone_patrimonio, v.cone_nome, v.cone_numero_serie ? `nº ${v.cone_numero_serie}` : null]
      .filter(Boolean).join(' · ');
  }
  if (v.cone_pelo_numero) {
    return [v.cone_patrimonio, v.cone_nome, `nº ${v.cone_codigo} · achado pelo número do arquivo`].filter(Boolean).join(' · ');
  }
  if (!v.cone_codigo) return null;
  return v.cone_numero_ambiguo
    ? `nº ${v.cone_codigo} do arquivo — casa com mais de um equipamento`
    : `nº ${v.cone_codigo} do arquivo — não está em Equipamentos`;
}

/**
 * O aviso do cone que não se ligou (onda 335): avisa e não impede — o relatório sai sem o certificado. Null quando
 * o cone está gravado, foi achado pelo número, ou o arquivo não diz o número.
 */
export function avisoDoCone(v: ConeDaVertical): string | null {
  if (v.cone_equipamento_id || v.cone_pelo_numero || !v.cone_codigo) return null;
  return v.cone_numero_ambiguo
    ? `O número ${v.cone_codigo} do arquivo casa com mais de um equipamento: escolha o cone. O relatório não fica `
      + 'impedido por isso.'
    : `nº ${v.cone_codigo} do arquivo — não está em Equipamentos. O relatório sai sem o certificado; cadastre o cone `
      + 'em Equipamentos para a rastreabilidade.';
}

/** O rascunho que abre a grade: os valores gravados, como a janela do cabeçalho os escreve. */
export function rascunhoDe(v: VerticalDaCampanha, fuso = 22): RascunhoDaVertical {
  const utm = utmGravado(v, fuso);
  return {
    na: paraCampo(v.nivel_agua, 2),
    cota: paraCampo(v.cota, 3),
    lat: paraCampo(v.latitude, 7),
    lon: paraCampo(v.longitude, 7),
    e: utm ? paraCampo(utm.e, 2) : '',
    n: utm ? paraCampo(utm.n, 2) : '',
    cone: { id: v.cone_equipamento_id, rotulo: v.cone_equipamento_id ? textoDoCone(v) : null },
    parada: v.motivo_parada ?? '',
    paradaTexto: v.motivo_parada_texto ?? '',
  };
}

type Lido = { valor: number | null; erro?: undefined } | { valor?: undefined; erro: string };

const arredondar = (n: number, casas: number) => Number(n.toFixed(casas));
const NAO_E_NUMERO = 'Não é um número: use vírgula para as casas, como 0,86.';

/** NA ou cota: vazio é «não informado»; a vírgula decimal brasileira e o ponto do GPS valem. */
export function lerMetros(texto: string, campo: 'na' | 'cota'): Lido {
  const t = texto.trim().replace(/−/g, '-').replace(/\s*m$/i, '');
  if (!t) return { valor: null };
  const n = paraNumero(t);
  if (n === undefined || !Number.isFinite(n)) return { erro: NAO_E_NUMERO };
  if (campo === 'na') {
    if (n < 0) return { erro: 'O NA é a profundidade da água: 0 ou mais.' };
    if (n > 500) return { erro: 'O NA vai até 500 m.' };
    return { valor: arredondar(n, 2) };
  }
  if (n < -500 || n > 9000) return { erro: 'A cota vai de −500 a 9.000 m.' };
  return { valor: arredondar(n, 3) };
}

/** O sinal e a letra dizem coisas opostas («N -27,3», «+48,6 O»). */
export const SINAL_CONTRA_LETRA = 'O sinal e a letra se contradizem: use só um dos dois, como −27,3498 ou 27,3498 S.';
/** A vírgula e o ponto juntos num grau («-27,3.4»): não há milhar num grau, e o ponto lido como milhar mudava o valor. */
export const DOIS_SEPARADORES = 'Use só a vírgula (ou só o ponto) como separador decimal, como −27,3498.';
/** Espaço entre os dígitos («0 30»): graus e minutos, ou dois números — não um grau decimal. */
export const ESPACO_NO_GRAU = 'Sem espaço no meio do número: graus decimais, como −27,3498.';

/**
 * Um grau decimal: «-27,3498», «-27.3498», «27,3498 S», «S 27,3498», «48,6384 W»
 * ou «48,6384 O» (sul e oeste negativos; N, L e E positivos). Arredondado a 7
 * casas — as da coluna (`numeric(10,7)`).
 *
 * Revisão da 333: o que é contraditório é recusado, em vez de virar uma
 * coordenada plausível e errada — o sinal «−» com N/L/E (ou o «+» explícito
 * com S/O/W), a vírgula e o ponto no mesmo grau (o ponto não é milhar aqui) e
 * o espaço entre dígitos («0 30» não é 30).
 */
export function lerGrau(texto: string, eixo: 'lat' | 'lon'): Lido {
  let t = texto.trim().replace(/−/g, '-').replace(/°/g, '').toUpperCase();
  if (!t) return { valor: null };
  let letra: string | null = null;
  const antes = /^([NSLOWE])\s*(.+)$/.exec(t);
  const depois = /^(.+?)\s*([NSLOWE])$/.exec(t);
  if (antes) { letra = antes[1]; t = antes[2]; } else if (depois) { letra = depois[2]; t = depois[1]; }
  if (letra) {
    if (eixo === 'lat' && !'NS'.includes(letra)) return { erro: 'Na latitude, a letra é S (sul) ou N (norte).' };
    if (eixo === 'lon' && !'LOWE'.includes(letra)) return { erro: 'Na longitude, a letra é O ou W (oeste), L ou E (leste).' };
  }
  t = t.trim();
  const sinal = /^([+-])\s*(.*)$/.exec(t);
  let negativo = false;
  if (sinal) {
    negativo = sinal[1] === '-';
    t = sinal[2];
    if (letra && negativo && 'NLE'.includes(letra)) return { erro: SINAL_CONTRA_LETRA };
    if (letra && !negativo && 'SOW'.includes(letra)) return { erro: SINAL_CONTRA_LETRA };
  }
  if (t.includes(',') && t.includes('.')) return { erro: DOIS_SEPARADORES };
  if (/\d\s+[\d,.]|[,.]\s+\d/.test(t)) return { erro: ESPACO_NO_GRAU };
  const n = /^[+-]/.test(t) ? undefined : paraNumero(t);
  if (n === undefined || !Number.isFinite(n)) return { erro: 'Não é um número: graus decimais, como −27,3498.' };
  const comSinal = negativo ? -n : n;
  const v = letra ? ('SOW'.includes(letra) ? -Math.abs(comSinal) : Math.abs(comSinal)) : comSinal;
  if (eixo === 'lat' && (v < -90 || v > 90)) return { erro: 'A latitude vai de −90 a 90.' };
  if (eixo === 'lon' && (v < -180 || v > 180)) return { erro: 'A longitude vai de −180 a 180.' };
  return { valor: arredondar(v, 7) };
}

/** As faixas do UTM no Brasil, as da amostra (0187/0190). */
export const FAIXA_DO_E = 'O E (UTM) vai de 100.000 a 900.000 m.';
export const FAIXA_DO_N = 'O N (UTM) vai de 1.000.000 a 10.000.000 m.';

/**
 * Um E ou um N de UTM, em metros, como a planilha de locação e o GPS os escrevem: «694.123,45»,
 * «694123,45», «694123.45», «6.968.000» (ponto de milhar sem vírgula: o UTM não tem grau para confundir),
 * com ou sem o «m» e a letra do eixo («694123 E», «6968000 N»). Arredondado ao centímetro.
 */
export function lerUtm(texto: string, eixo: 'e' | 'n'): Lido {
  let t = texto.trim().replace(/−/g, '-').toUpperCase().replace(/\s*M$/, '');
  if (!t) return { valor: null };
  const letra = /^([EN])\s*(.+)$/.exec(t) ?? /^(.+?)\s*([EN])$/.exec(t);
  if (letra) {
    const l = /^[EN]$/.test(letra[1]) ? letra[1] : letra[2];
    t = (/^[EN]$/.test(letra[1]) ? letra[2] : letra[1]).trim();
    if (l !== eixo.toUpperCase()) return { erro: eixo === 'e' ? 'Na coluna E vai o E (leste); o N vai na coluna ao lado.' : 'Na coluna N vai o N (norte); o E vai na coluna ao lado.' };
  }
  if (t.startsWith('-')) return { erro: 'O UTM não é negativo: E e N em metros, como 694.123,45.' };
  if (/\d\s+\d/.test(t) && !/^\d{1,3}( \d{3})+(,\d+)?$/.test(t)) return { erro: 'Sem espaço no meio do número: como 694.123,45.' };
  t = t.replace(/ /g, '');
  let n: number;
  if (t.includes(',')) n = Number(t.replace(/\./g, '').replace(',', '.'));
  else if (/^\d{1,3}(\.\d{3})+$/.test(t)) n = Number(t.replace(/\./g, ''));
  else n = Number(t);
  if (!/^[\d.,]+$/.test(t) || !Number.isFinite(n)) return { erro: 'Não é um número: metros, como 694.123,45.' };
  if (eixo === 'e' && (n < 100000 || n > 900000)) return { erro: FAIXA_DO_E };
  if (eixo === 'n' && (n < 1000000 || n > 10000000)) return { erro: FAIXA_DO_N };
  return { valor: arredondar(n, 2) };
}

/** Os erros de uma linha, por campo (só os que há). */
export type ErrosDaLinha = Partial<Record<ColunaDeTexto | 'paradaTexto', string>>;

/** A coordenada inteira ou nenhuma — a frase da janela do cabeçalho. */
export const MEIA_COORDENADA = 'A coordenada vem inteira (latitude e longitude) ou não vem.';

/** O E e o N juntos, ou nenhum dos dois. */
export const MEIO_UTM = 'O E e o N andam juntos: os dois, ou nenhum.';

/** Os erros de uma linha da grade, célula a célula, com a frase em português — só as colunas do sistema escolhido. */
export function errosDaLinha(r: RascunhoDaVertical, modo: ModoDaCoordenada = 'graus'): ErrosDaLinha {
  const e: ErrosDaLinha = {};
  const na = lerMetros(r.na, 'na');
  const cota = lerMetros(r.cota, 'cota');
  if (na.erro) e.na = na.erro;
  if (cota.erro) e.cota = cota.erro;
  if (modo === 'utm') {
    const ue = lerUtm(r.e, 'e');
    const un = lerUtm(r.n, 'n');
    if (ue.erro) e.e = ue.erro;
    if (un.erro) e.n = un.erro;
    if (!ue.erro && !un.erro && (ue.valor === null) !== (un.valor === null)) e[ue.valor === null ? 'e' : 'n'] = MEIO_UTM;
  } else {
    const lat = lerGrau(r.lat, 'lat');
    const lon = lerGrau(r.lon, 'lon');
    if (lat.erro) e.lat = lat.erro;
    if (lon.erro) e.lon = lon.erro;
    if (!lat.erro && !lon.erro && (lat.valor === null) !== (lon.valor === null)) {
      e[lat.valor === null ? 'lat' : 'lon'] = MEIA_COORDENADA;
    }
  }
  if (r.parada === 'outro' && !r.paradaTexto.trim()) e.paradaTexto = 'Com o encerramento «outro motivo», escreva qual.';
  return e;
}

const m2 = (v: number | null) => (v === null ? null : `${numero(v, 2)} m`);

/** O UTM por extenso: «694.123,45 E; 6.968.000,00 N (22S)». */
export function textoDoUtm(e: number | null, n: number | null, fuso: number | null): string {
  return e === null || n === null || fuso === null ? '' : `${numero(e, 2)} E; ${numero(n, 2)} N (${fuso}S)`;
}

/** A coordenada do rascunho difere da gravada, no sistema da grade (enquanto há, o sistema não troca). */
export function coordenadaMudou(v: VerticalDaCampanha, r: RascunhoDaVertical, s: SistemaDaCoordenada): boolean {
  if (s.modo === 'utm') {
    const g = utmGravado(v, s.fuso);
    const ue = lerUtm(r.e, 'e');
    const un = lerUtm(r.n, 'n');
    if (ue.erro || un.erro) return true;
    if (g === null) return ue.valor !== null || un.valor !== null;
    return ue.valor !== g.e || un.valor !== g.n;
  }
  const lat = lerGrau(r.lat, 'lat');
  const lon = lerGrau(r.lon, 'lon');
  if (lat.erro || lon.erro) return true;
  return lat.valor !== v.latitude || lon.valor !== v.longitude;
}

/** O rascunho com a coordenada de volta à gravada, no sistema novo (ao trocar o sistema ou o fuso). */
export function comCoordenadaGravada(v: VerticalDaCampanha, r: RascunhoDaVertical, fuso: number): RascunhoDaVertical {
  const b = rascunhoDe(v, fuso);
  return { ...r, lat: b.lat, lon: b.lon, e: b.e, n: b.n };
}
const m3 = (v: number | null) => (v === null ? null : `${numero(v, 3, true)} m`);

/**
 * O que mudou numa linha válida: o corpo do lote (só o que mudou, como o
 * PATCH da vertical) e as frases da janela do motivo («NA não informado →
 * 0,86 m»). `null` quando nada mudou.
 */
export function mudancasDa(v: VerticalDaCampanha, r: RascunhoDaVertical,
  s: SistemaDaCoordenada = { modo: 'graus', fuso: 22 }):
  { corpo: VerticalDoLote; frases: string[]; campos: Set<string> } | null {
  const corpo: VerticalDoLote = { id: v.id };
  const frases: string[] = [];
  const campos = new Set<string>();
  const na = lerMetros(r.na, 'na').valor ?? null;
  const cota = lerMetros(r.cota, 'cota').valor ?? null;
  if (na !== v.nivel_agua) {
    corpo.nivelAgua = na; campos.add('na');
    frases.push(`NA ${m2(v.nivel_agua) ?? 'não informado'} → ${m2(na) ?? 'não informado'}`);
  }
  if (cota !== v.cota) {
    corpo.cota = cota; campos.add('cota');
    frases.push(`cota ${m3(v.cota) ?? '—'} → ${m3(cota) ?? '—'}`);
  }
  if (s.modo === 'utm') {
    /* Onda 334: E, N e o fuso da grade vão juntos, e o banco converte; os dois vazios tiram a coordenada — só
       quando ela está neste fuso (a de outro fuso não aparece na célula, e a célula vazia não a apaga). */
    const g = utmGravado(v, s.fuso);
    const e = lerUtm(r.e, 'e').valor ?? null;
    const n = lerUtm(r.n, 'n').valor ?? null;
    const antes = g ? textoDoUtm(g.e, g.n, s.fuso) : coordenada(v.latitude, v.longitude);
    if (e !== null && n !== null && (g === null || e !== g.e || n !== g.n)) {
      corpo.utmE = e; corpo.utmN = n; corpo.utmFuso = s.fuso;
      if (g === null || e !== g.e) campos.add('e');
      if (g === null || n !== g.n) campos.add('n');
      frases.push(`coordenada ${antes || '—'} → ${textoDoUtm(e, n, s.fuso)}`);
    } else if (e === null && n === null && g !== null) {
      corpo.latitude = null; corpo.longitude = null;
      campos.add('e'); campos.add('n');
      frases.push(`coordenada ${antes} → —`);
    }
  } else {
    const lat = lerGrau(r.lat, 'lat').valor ?? null;
    const lon = lerGrau(r.lon, 'lon').valor ?? null;
    if (lat !== v.latitude || lon !== v.longitude) {
      corpo.latitude = lat; corpo.longitude = lon;
      if (lat !== v.latitude) campos.add('lat');
      if (lon !== v.longitude) campos.add('lon');
      frases.push(`coordenada ${coordenada(v.latitude, v.longitude) || '—'} → ${coordenada(lat, lon) || '—'}`);
    }
  }
  if (r.cone.id !== v.cone_equipamento_id) {
    corpo.coneEquipamentoId = r.cone.id; campos.add('cone');
    frases.push(`cone ${textoDoCone(v) ?? '—'} → ${r.cone.id ? r.cone.rotulo ?? 'outro do parque' : 'nenhum do parque'}`);
  }
  const parada = r.parada || null;
  const paradaTexto = r.paradaTexto.trim() || null;
  const mudouParada = parada !== v.motivo_parada;
  const mudouTexto = paradaTexto !== (v.motivo_parada_texto ?? null);
  if (mudouParada) corpo.motivoParada = parada;
  if (mudouTexto) corpo.motivoParadaTexto = paradaTexto;
  if (mudouParada || mudouTexto) {
    campos.add('parada');
    frases.push(`encerramento ${textoDoEncerramento(v.motivo_parada, v.motivo_parada_texto) ?? '—'} → ${
      textoDoEncerramento(parada, paradaTexto) ?? '—'}`);
  }
  return frases.length ? { corpo, frases, campos } : null;
}

/**
 * O bloco copiado da planilha (Excel, LibreOffice, Google): linhas por quebra,
 * células por tabulação. `null` quando é uma célula só — aí a colagem é a do
 * navegador, no campo.
 */
export function blocoColado(texto: string): string[][] | null {
  const t = texto.replace(/\r\n?/g, '\n').replace(/\n+$/, '');
  if (!t.includes('\t') && !t.includes('\n')) return null;
  return t.split('\n').map((l) => l.split('\t'));
}

/**
 * Distribui o bloco a partir da célula (`linha`, `coluna`): pelas linhas
 * seguintes e pelas colunas à direita, na ordem da grade (NA, cota e a
 * coordenada no sistema escolhido — latitude e longitude, ou E e N). O que
 * passa da última linha ou da última coluna fica de fora e é contado.
 */
export function distribuir(
  rascunhos: RascunhoDaVertical[], linha: number, coluna: ColunaDeTexto, bloco: string[][],
  colunas: readonly ColunaDeTexto[] = colunasDoModo('graus'),
): { rascunhos: RascunhoDaVertical[]; linhasDeFora: number; celulasDeFora: number } {
  const novos = rascunhos.map((r) => ({ ...r }));
  const COLUNAS_DE_TEXTO = colunas;
  const c0 = COLUNAS_DE_TEXTO.indexOf(coluna);
  let linhasDeFora = 0;
  let celulasDeFora = 0;
  bloco.forEach((celulas, i) => {
    const alvo = novos[linha + i];
    if (!alvo) { linhasDeFora += 1; return; }
    celulas.forEach((valor, j) => {
      const col = COLUNAS_DE_TEXTO[c0 + j];
      if (!col) { celulasDeFora += 1; return; }
      alvo[col] = valor.trim();
    });
  });
  return { rascunhos: novos, linhasDeFora, celulasDeFora };
}
